/**
 * Cadastro de produto — a tela que GERA A GRADE.
 *
 * O que a diferencia de um formulário comum: as pills de tamanho e cor não
 * são campos, são a definição de quantos SKUs vão existir. Três cores e quatro
 * tamanhos são doze variações vendáveis, cada uma com saldo, preço, custo e
 * código de barras próprios — e é isso que o rail da direita mostra ao vivo,
 * antes de salvar.
 *
 * O formulário em si vive em `FormularioProduto.tsx`, compartilhado com a
 * edição: as duas telas editam a mesma coisa, e duplicar o arquivo faria
 * "acrescentar campo" virar duas tarefas.
 *
 * As FOTOS aqui ficam pendentes até o produto existir — não há a que anexá-las
 * antes do POST. Elas sobem logo depois, na ordem em que estão na tela, e a
 * primeira vira a capa. Se alguma falhar, o produto continua cadastrado e a
 * tela diz qual não subiu: perder o cadastro inteiro porque uma imagem falhou
 * seria trocar um problema pequeno por um grande.
 *
 * A tela NÃO tem o bloco "Fiscal" do protótipo: NCM, CFOP, origem e unidade não
 * têm coluna no banco. Campo que não persiste é pior que campo ausente —
 * ensina que o dado foi salvo.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { useSessao } from '../../plataforma/contexto-sessao'
import {
  cadastrarProduto,
  enviarFoto,
  motivoParaRecusarFoto,
  MAX_FOTOS,
  type Cor,
  type ResultadoCadastro,
} from '../../plataforma/produtos'
import { LayoutApp } from '../app/LayoutApp'
import { BlocoDeFotos, FormularioProduto, type FotoNaTela } from './FormularioProduto'
import { valoresVazios, PALETA, type ValoresProduto } from './valores'

/** Uma foto escolhida que ainda não subiu: o produto não existe. */
type FotoPendente = {
  chave: string
  arquivo: File
  url: string
}

export function TelaCadastroProduto() {
  const { podeVer } = useSessao()
  const veCusto = podeVer('cost.read')
  const navegar = useNavigate()

  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState('')
  const [porCampo, setPorCampo] = useState<Record<string, string>>({})
  const [criado, setCriado] = useState<ResultadoCadastro | null>(null)
  const [fotosEnviadas, setFotosEnviadas] = useState(0)
  const [erroDeFoto, setErroDeFoto] = useState('')
  const [pendentes, setPendentes] = useState<FotoPendente[]>([])
  const [inicial, setInicial] = useState<ValoresProduto>(valoresVazios)

  // Cada miniatura pendente é um `blob:` que o navegador só libera quando
  // alguém pede. Sem a limpeza, cadastrar dez produtos numa sessão deixa dez
  // imagens presas na memória da aba.
  //
  // A limpeza é só na DESMONTAGEM, e por isso a lista chega por `ref`: com
  // `pendentes` na lista de dependências, acrescentar a segunda foto revogaria
  // a URL da primeira — que continua na tela — e a miniatura viraria um
  // quadrado quebrado.
  const listaAtual = useRef<FotoPendente[]>([])
  listaAtual.current = pendentes
  useEffect(() => {
    return () => {
      for (const foto of listaAtual.current) URL.revokeObjectURL(foto.url)
    }
  }, [])

  const escolherFotos = (arquivos: File[]) => {
    setErroDeFoto('')

    const aceitas: FotoPendente[] = []
    const recusas: string[] = []

    for (const arquivo of arquivos) {
      if (pendentes.length + aceitas.length >= MAX_FOTOS) {
        recusas.push(`O limite é de ${MAX_FOTOS} fotos por produto.`)
        break
      }
      const motivo = motivoParaRecusarFoto(arquivo)
      if (motivo) {
        recusas.push(motivo)
        continue
      }
      aceitas.push({
        chave: `${arquivo.name}-${arquivo.size}-${aceitas.length}-${Date.now()}`,
        arquivo,
        url: URL.createObjectURL(arquivo),
      })
    }

    if (aceitas.length > 0) setPendentes((atual) => [...atual, ...aceitas])
    if (recusas.length > 0) setErroDeFoto(recusas.join(' '))
  }

  const removerPendente = (chave: string) => {
    setPendentes((atual) => {
      const foto = atual.find((item) => item.chave === chave)
      if (foto) URL.revokeObjectURL(foto.url)
      return atual.filter((item) => item.chave !== chave)
    })
    setErroDeFoto('')
  }

  /** Marcar capa aqui é reordenar a fila: a ordem de envio é a ordem da tela. */
  const promoverPendente = (chave: string) => {
    setPendentes((atual) => {
      const escolhida = atual.find((item) => item.chave === chave)
      if (!escolhida) return atual
      return [escolhida, ...atual.filter((item) => item.chave !== chave)]
    })
  }

  const enviar = async (valores: ValoresProduto) => {
    setEnviando(true)
    setErro('')
    setPorCampo({})
    setErroDeFoto('')
    try {
      const saida = await cadastrarProduto({
        nome: valores.nome.trim(),
        codigo: valores.codigo.trim() || undefined,
        categoria: valores.categoria,
        marca: valores.marca.trim() || undefined,
        preco: valores.preco.trim(),
        custo: veCusto && valores.custo.trim() !== '' ? valores.custo.trim() : undefined,
        // O mínimo vazio some do pedido: assim o SKU herda o default 2 da
        // migração 0007 em vez de virar zero por causa de um campo apagado.
        estoque_minimo: valores.minimo.trim() === '' ? undefined : Number(valores.minimo),
        // A ordem das pills define a ordem da grade gerada, e a grade sai
        // sempre na mesma sequência — o lojista confere olhando.
        cores: valores.cores.map(
          (nomeDaCor) => PALETA.find((cor) => cor.nome === nomeDaCor) ?? ({ nome: nomeDaCor } as Cor),
        ),
        tamanhos: valores.tamanhos,
      })

      // O produto já está gravado. Daqui em diante, falha de foto NÃO desfaz o
      // cadastro — ela vira aviso.
      const { enviadas, falhas } = await subirPendentes(saida.codigo, pendentes)
      setFotosEnviadas(enviadas)
      if (falhas.length > 0) setErroDeFoto(falhas.join(' '))
      for (const foto of pendentes) URL.revokeObjectURL(foto.url)
      setPendentes([])

      setCriado(saida)
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setErro('Não foi possível cadastrar o produto.')
        return
      }
      setErro(causa.message)
      setPorCampo(causa.campos)
    } finally {
      setEnviando(false)
    }
  }

  if (criado) {
    return (
      <LayoutApp
        titulo="Produto cadastrado"
        subtitulo={`${criado.codigo} · ${criado.skus} ${criado.skus === 1 ? 'SKU' : 'SKUs'} gerados`}
      >
        <ResumoDoCadastro
          criado={criado}
          fotosEnviadas={fotosEnviadas}
          erroDeFoto={erroDeFoto}
          aoNovo={() => {
            setCriado(null)
            setFotosEnviadas(0)
            setErroDeFoto('')
            // Categoria e marca ficam: quem cadastra em lote cadastra o mesmo
            // tipo de peça em sequência.
            setInicial((atual) => ({
              ...valoresVazios(),
              categoria: atual.categoria,
              marca: atual.marca,
              minimo: atual.minimo,
            }))
          }}
        />
      </LayoutApp>
    )
  }

  const daTela: FotoNaTela[] = pendentes.map((foto, i) => ({
    chave: foto.chave,
    url: foto.url,
    nome: foto.arquivo.name,
    tamanho: foto.arquivo.size,
    capa: i === 0,
    pendente: true,
  }))

  return (
    <LayoutApp
      trilha={[{ rotulo: 'Produtos', para: '/app/produtos' }, { rotulo: 'Novo produto' }]}
      voltarPara="/app/produtos"
      titulo="Novo produto"
      subtitulo="Cada cruzamento de cor e tamanho gera um SKU próprio"
    >
      <FormularioProduto
        modo="cadastro"
        inicial={inicial}
        veCusto={veCusto}
        enviando={enviando}
        erro={erro}
        porCampo={porCampo}
        aoEnviar={(valores) => void enviar(valores)}
        aoCancelar={() => navegar('/app/produtos')}
        rotuloDoBotao={(variacoes) =>
          enviando
            ? 'Gerando a grade…'
            : variacoes > 0
              ? `Cadastrar e gerar ${variacoes} ${variacoes === 1 ? 'SKU' : 'SKUs'}`
              : 'Cadastrar produto'
        }
        blocoDeFotos={
          <BlocoDeFotos
            fotos={daTela}
            erro={erroDeFoto}
            ocupado={enviando}
            aoEscolher={escolherFotos}
            aoRemover={(foto) => removerPendente(foto.chave)}
            aoMarcarCapa={(foto) => promoverPendente(foto.chave)}
            rodape={
              pendentes.length > 0 ? (
                <p className="mt-3 text-[12.5px] text-texto-suave">
                  As fotos sobem depois que o produto for criado — ele precisa existir para
                  recebê-las.
                </p>
              ) : undefined
            }
          />
        }
        avisoDoRail={
          /*
            O protótipo mostra "Estoque inicial", "Investido em custo" e
            "Receita potencial" aqui, calculados a partir de uma matriz de
            estoque inicial no formulário. O cadastro NÃO lança estoque: saldo
            só muda por movimento (INV-04), e o caminho para o primeiro
            movimento é a aba de ajuste do estoque ou o recebimento de compra.
            Prometer estoque inicial aqui gravaria saldo sem movimento —
            exatamente o que a migração 0006 impede.
          */
          <div className="mt-3 flex gap-2.5 rounded-sistema border border-borda bg-fundo px-3.5 py-3">
            <Icone nome="info" tamanho={19} className="text-texto-fraco" />
            <p className="text-[12.5px] leading-[1.55] text-texto-suave">
              O produto nasce com a grade zerada. A primeira entrada de peças é lançada em Estoque,
              e fica registrada com responsável e motivo.
            </p>
          </div>
        }
      />
    </LayoutApp>
  )
}

/**
 * Sobe as fotos pendentes, em série e na ordem da tela.
 *
 * Em SÉRIE de propósito: a ordem no banco é a ordem de chegada, e disparar seis
 * uploads em paralelo entregaria a capa em qualquer posição. O custo é tempo de
 * espera em algo que acontece uma vez por cadastro.
 */
async function subirPendentes(
  codigo: string,
  pendentes: FotoPendente[],
): Promise<{ enviadas: number; falhas: string[] }> {
  let enviadas = 0
  const falhas: string[] = []

  for (const foto of pendentes) {
    try {
      await enviarFoto(codigo, foto.arquivo)
      enviadas++
    } catch (causa: unknown) {
      const motivo = causa instanceof ErroApi ? causa.message : 'falha ao enviar.'
      falhas.push(`"${foto.arquivo.name}": ${motivo}`)
    }
  }

  return { enviadas, falhas }
}

/* ---------------------------------------------------------- pós-cadastro */

/**
 * O que a tela mostra depois de salvar.
 *
 * Lista as variações com o código de barras porque a pessoa acabou de criar N
 * SKUs que precisam de N etiquetas — e a tela de etiquetas não existe ainda.
 * Ver o código na tela permite bipar e conferir agora.
 */
function ResumoDoCadastro({
  criado,
  fotosEnviadas,
  erroDeFoto,
  aoNovo,
}: {
  criado: ResultadoCadastro
  fotosEnviadas: number
  erroDeFoto: string
  aoNovo: () => void
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      <div
        role="status"
        className="flex gap-3 rounded-sistema border border-ok-suave bg-ok-suave px-4 py-3.5"
      >
        <Icone nome="check_circle" tamanho={20} className="text-ok" />
        <p className="text-[13px] leading-[1.55] text-ok">
          <strong className="font-semibold">{criado.nome}</strong> cadastrado com {criado.skus}{' '}
          {criado.skus === 1 ? 'variação' : 'variações'} — {criado.cores}{' '}
          {criado.cores === 1 ? 'cor' : 'cores'} × {criado.tamanhos}{' '}
          {criado.tamanhos === 1 ? 'tamanho' : 'tamanhos'}
          {fotosEnviadas > 0 &&
            ` e ${fotosEnviadas} ${fotosEnviadas === 1 ? 'foto' : 'fotos'}`}
          . A grade nasce zerada: lance a primeira entrada em Estoque.
        </p>
      </div>

      {/* Falha de foto não desfaz o cadastro: o produto está lá, e a tela diz
          o que faltou para a pessoa reenviar pela edição. */}
      {erroDeFoto && (
        <div
          role="alert"
          className="flex gap-3 rounded-sistema border border-atencao-suave bg-atencao-fundo px-4 py-3.5"
        >
          <Icone nome="warning" tamanho={20} className="text-atencao-texto" />
          <p className="text-[13px] leading-[1.55] text-atencao-texto">
            O produto foi cadastrado, mas nem toda foto subiu: {erroDeFoto} Reenvie pela edição do
            produto.
          </p>
        </div>
      )}

      <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-borda px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-tinta">Variações geradas</p>
            <p className="tabular text-[12.5px] text-texto-suave">
              {criado.codigo} · mínimo de {criado.estoque_minimo} por tamanho
            </p>
          </div>
          <div className="flex gap-2.5">
            <Botao variante="secundario" icone="add" onClick={aoNovo}>
              Cadastrar outro
            </Botao>
            <LinkBotao
              para={`/app/produtos/${encodeURIComponent(criado.codigo)}/editar`}
              variante="secundario"
              icone="edit_note"
            >
              Editar
            </LinkBotao>
            <LinkBotao para="/app/produtos">Ver no catálogo</LinkBotao>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse">
            <caption className="sr-only">
              SKUs criados para {criado.nome}, com código de barras
            </caption>
            <thead>
              <tr className="bg-fundo">
                {['Cor', 'Tamanho', 'Código de barras'].map((titulo) => (
                  <th
                    key={titulo}
                    scope="col"
                    className="border-b border-borda px-3 py-2.5 text-left text-[12.5px] font-semibold text-texto-suave"
                  >
                    {titulo}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {criado.variacoes.map((variacao) => (
                <tr key={variacao.codigo_barras}>
                  <td className="border-b border-divisoria px-3 py-2 text-[13px] text-tinta">
                    <span className="flex items-center gap-2">
                      {variacao.hex && (
                        <span
                          aria-hidden
                          style={{ background: variacao.hex }}
                          className="h-3.5 w-3.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
                        />
                      )}
                      {variacao.cor}
                    </span>
                  </td>
                  <td className="tabular border-b border-divisoria px-3 py-2 text-[13px] font-semibold text-tinta">
                    {variacao.tamanho}
                  </td>
                  <td className="tabular border-b border-divisoria px-3 py-2 text-[13px] text-texto-suave">
                    {variacao.codigo_barras}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
